import type { Section } from '@/api/types/core/sections';
import type { Translation } from '@/api/types/core/translations';
import type { SchemaTranslationWithDetails } from '@/api/schemaTranslations';
import type { SchemaJson, SectionJson } from '@/api/types/core/schemaJson';
import type { SchemaWithSectionList } from '@/stores/schemasStore';
import type { FieldConfigResponse } from '@/api/field_config';

export interface CompiledTranslation {
  titre: string;
  description?: string;
}

export interface CompiledTranslations {
  [languageCode: string]: CompiledTranslation;
}

/**
 * Extension de SectionJson avec section_id et indentationLevel optionnels
 */
export interface SectionJsonWithId extends SectionJson {
  section_id?: number;
  indentationLevel?: number;
}

/**
 * Extension de SchemaJson avec sections qui peuvent avoir section_id
 */
export interface SchemaJsonWithIds extends Omit<SchemaJson, 'section_list'> {
  section_list: SectionJsonWithId[];
}

/**
 * Fusionne intelligemment les traductions de section et de schéma
 * @param sectionTranslations - Traductions par défaut de la section
 * @param schemaTranslations - Traductions spécifiques au schéma
 * @returns Traductions compilées avec logique de fusion
 */
export function mergeTranslations(
  sectionTranslations: (Translation & { reference_id?: number })[],
  schemaTranslations: SchemaTranslationWithDetails[]
): CompiledTranslations {
  const compiledTranslations: CompiledTranslations = {};

  // 1. D'abord, ajouter toutes les traductions de section comme base
  sectionTranslations.forEach(sectionTrad => {
    compiledTranslations[sectionTrad.language_code] = {
      titre: sectionTrad.titre,
      description: sectionTrad.description || undefined
    };
  });

  // 2. Ensuite, fusionner avec les traductions schema selon la logique demandée
  schemaTranslations.forEach(schemaTrad => {
    const existingSectionTrad = compiledTranslations[schemaTrad.language_code];
    
    if (existingSectionTrad) {
      // Cas complexe : fusion intelligente titre/description
      const schemaHasTitle = schemaTrad.titre && schemaTrad.titre.trim() !== '';
      const schemaHasDescription = schemaTrad.description && schemaTrad.description.trim() !== '';
      
      if (!schemaHasTitle && schemaHasDescription) {
        // Cas spécial : schema a description mais pas de titre
        // → Garder le titre de la section, utiliser la description du schema
        compiledTranslations[schemaTrad.language_code] = {
          titre: existingSectionTrad.titre, // Titre de la section
          description: schemaTrad.description // Description du schema
        };
      } else {
        // Cas normal : la traduction schema prend la priorité
        compiledTranslations[schemaTrad.language_code] = {
          titre: schemaHasTitle ? schemaTrad.titre : existingSectionTrad.titre,
          description: schemaHasDescription ? schemaTrad.description : existingSectionTrad.description
        };
      }
    } else {
      // Pas de traduction de section pour cette langue, utiliser uniquement le schema
      compiledTranslations[schemaTrad.language_code] = {
        titre: schemaTrad.titre,
        description: schemaTrad.description || undefined
      };
    }
  });

  return compiledTranslations;
}

/**
 * Compile un schéma complet avec toutes ses sections et traductions fusionnées
 * @param schema - Le schéma à compiler
 * @param allSections - Toutes les sections disponibles
 * @param allTranslations - Toutes les traductions de sections
 * @param allSchemaTranslations - Toutes les traductions spécifiques aux schémas
 * @param allConfigs - Toutes les configurations de champs
 * @param includeSectionIds - Si true, inclut l'ID numérique des sections dans "section_id"
 * @returns Le schéma compilé prêt pour export
 */
export function compileSchemaForExport(
  schema: SchemaWithSectionList,
  allSections: Section[],
  allTranslations: (Translation & { reference_id?: number })[],
  allSchemaTranslations: SchemaTranslationWithDetails[],
  allConfigs: FieldConfigResponse[],
  includeSectionIds: boolean = false
): SchemaJsonWithIds {
  // Filtrer les sections du schéma
  const schemaSections = allSections.filter(section => 
    schema.section_list.includes(section.id)
  );

  console.log(`🔍 schemaExportUtils - Schema section_list: [${schema.section_list.join(', ')}]`);
  console.log(`🔍 schemaExportUtils - Sections filtrées (${schemaSections.length}):`, schemaSections.map(s => `${s.id}:${s.name}(${s.type})`));

  // Trier les sections hiérarchiquement
  const sortedSchemaSections = sortSectionsHierarchically(schemaSections);

  // Compiler chaque section avec ses traductions fusionnées
  const sectionJsonList: SectionJsonWithId[] = sortedSchemaSections.map(section => {
    // Traductions par défaut de la section
    const sectionTranslations = allTranslations.filter(t => 
      t.reference_id === section.id
    );

    // Traductions spécifiques au schéma pour cette section
    const schemaSpecificTranslations = allSchemaTranslations.filter(t => 
      t.schema_model.uuid === schema.uuid && t.section.id === section.id
    );

    // Configurations de la section
    const sectionConfigs = allConfigs.filter(c => 
      c.reference_id === section.id
    );

    // Construire la section JSON
    const sectionJson: SectionJsonWithId = {
      id: section.name
    };

    // Ajouter l'ID numérique de la section et le niveau d'indentation si demandé
    if (includeSectionIds) {
      sectionJson.section_id = section.id;
      // Calculer le niveau d'indentation basé sur le nombre de points dans le nom
      sectionJson.indentationLevel = section.name.split('.').length - 1;
    }

    // Ajouter le type si présent
    if (section.type && section.type.trim() !== '') {
      sectionJson.type = section.type;
    }

    // Ajouter display si false
    if (!section.display) {
      sectionJson.display = section.display;
    }

    // 🚀 FUSION INTELLIGENTE DES TRADUCTIONS
    const compiledTranslations = mergeTranslations(sectionTranslations, schemaSpecificTranslations);
    
    // Ajouter les traductions compilées seulement s'il y en a
    if (Object.keys(compiledTranslations).length > 0) {
      sectionJson.translations = compiledTranslations;
    }

    // Ajouter les conditions si présentes
    if (sectionConfigs.length > 0) {
      sectionJson.conditions = sectionConfigs.map(c => {
        const conditionSection = allSections.find(s => s.id === c.condition_id);
        return {
          condition_id: conditionSection?.name || `unknown_section_${c.condition_id}`,
          condition_value: c.condition_value || ''
        };
      });
    }

    return sectionJson;
  });

  // Retourner le schéma compilé
  return {
    uuid: schema.uuid,
    appellation: schema.appellation,
    type: schema.type,
    description: schema.description || '',
    version: new Date().toISOString(),
    section_list: sectionJsonList
  };
}

/**
 * Trie les sections de manière hiérarchique
 * @param sections - Sections à trier
 * @returns Sections triées hiérarchiquement
 */
function sortSectionsHierarchically(sections: Section[]): Section[] {
  const result: Section[] = [];
  const processed = new Set<string>();
  
  const addSectionAndChildren = (parentPath: string): void => {
    const children = sections.filter(s => {
      const parentPrefix = parentPath ? parentPath + '.' : '';
      const isDirectChild = parentPath === '' 
        ? !s.name.includes('.')
        : s.name.startsWith(parentPrefix) && 
          !s.name.substring(parentPrefix.length).includes('.');
      
      return isDirectChild && !processed.has(s.name);
    });
    
    children.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
    
    children.forEach(child => {
      if (!processed.has(child.name)) {
        result.push(child);
        processed.add(child.name);
        addSectionAndChildren(child.name);
      }
    });
  };
  
  addSectionAndChildren('');
  return result;
}

/**
 * Génère un nom de fichier pour l'export
 * @param schema - Le schéma à exporter
 * @returns Nom de fichier sécurisé
 */
export function generateExportFilename(schema: SchemaWithSectionList): string {
  const sanitizedName = schema.appellation.replace(/[^a-zA-Z0-9]/g, '_');
  const dateString = new Date().toISOString().slice(0, 10);
  return `schema_${sanitizedName}_${dateString}.json`;
}

/**
 * Compile un schéma avec les IDs numériques des sections inclus
 * @param schema - Le schéma à compiler
 * @param allSections - Toutes les sections disponibles
 * @param allTranslations - Toutes les traductions de sections
 * @param allSchemaTranslations - Toutes les traductions spécifiques aux schémas
 * @param allConfigs - Toutes les configurations de champs
 * @returns Le schéma compilé avec les section_id inclus
 */
export function compileSchemaWithIds(
  schema: SchemaWithSectionList,
  allSections: Section[],
  allTranslations: (Translation & { reference_id?: number })[],
  allSchemaTranslations: SchemaTranslationWithDetails[],
  allConfigs: FieldConfigResponse[]
): SchemaJsonWithIds {
  return compileSchemaForExport(
    schema,
    allSections,
    allTranslations,
    allSchemaTranslations,
    allConfigs,
    true  // includeSectionIds = true
  );
}

/**
 * Compte les traductions spécifiques à un schéma
 * @param schemaUuid - UUID du schéma
 * @param allSchemaTranslations - Toutes les traductions de schémas
 * @returns Nombre de traductions spécifiques au schéma
 */
export function countSchemaSpecificTranslations(
  schemaUuid: string, 
  allSchemaTranslations: SchemaTranslationWithDetails[]
): number {
  return allSchemaTranslations.filter(t => t.schema_model.uuid === schemaUuid).length;
} 